import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { sendEmail } from "../../services/EmailService";
import "./BrokerMessage.css";

const BrokerMessage = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        company: "",
        message: ""
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [error, setError] = useState("");

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const validate = () => {
        const errs = {};
        if (!formData.name) errs.name = "Name required";
        if (!formData.email) errs.email = "Email required";
        if (!formData.phone) errs.phone = "Phone required";
        if (!formData.company) errs.company = "Company required";
        if (!formData.message) errs.message = "Message required";
        return errs;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccessMessage("");
        setIsSubmitting(true);

        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setError("Please fill in all required fields.");
            setIsSubmitting(false);
            return;
        }

        try {
            await sendEmail({
                recipientEmail: [
                    process.env.REACT_APP_RECIPIENT_EMAIL,
                    process.env.REACT_APP_RECIPIENT_EMAIL2,
                    process.env.REACT_APP_RECIPIENT_EMAIL3,
                    process.env.REACT_APP_RECIPIENT_EMAIL4
                ],
                senderEmail: process.env.REACT_APP_SENDER_EMAIL,
                subject: `Brokerage Inquiry from ${formData.name}`,
                content: `
                    <h2>Brokerage Contact Inquiry</h2>
                    <table style="border-collapse:collapse;width:100%;font-family:sans-serif;font-size:15px;">
                        <tr><th style="background:#f5f5f5;text-align:left;padding:8px;border-bottom:1px solid #ddd;">Name</th><td style="padding:8px;border-bottom:1px solid #eee;">${formData.name}</td></tr>
                        <tr><th style="background:#f5f5f5;text-align:left;padding:8px;border-bottom:1px solid #ddd;">Email</th><td style="padding:8px;border-bottom:1px solid #eee;">${formData.email}</td></tr>
                        <tr><th style="background:#f5f5f5;text-align:left;padding:8px;border-bottom:1px solid #ddd;">Phone</th><td style="padding:8px;border-bottom:1px solid #eee;">${formData.phone}</td></tr>
                        <tr><th style="background:#f5f5f5;text-align:left;padding:8px;border-bottom:1px solid #ddd;">Company</th><td style="padding:8px;border-bottom:1px solid #eee;">${formData.company}</td></tr>
                        <tr><th style="background:#f5f5f5;text-align:left;padding:8px;border-bottom:1px solid #ddd;">Message</th><td style="padding:8px;border-bottom:1px solid #eee;">${formData.message}</td></tr>
                    </table>
                `,
                senderName: formData.name,
                replyTo: formData.email
            });
            setSuccessMessage("Your message has been sent! We will contact you soon.");
            setFormData({
                name: "",
                email: "",
                phone: "",
                company: "",
                message: ""
            });
        } catch (err) {
            setError("There was an error sending your request. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Brokerage Contact | AptoWebsites</title>
                <meta name="description" content="Contact AptoWebsites for custom brokerage solutions and pricing." />
                <meta name="keywords" content="Brokerage, Contact, Web Development, AptoWebsites" />
                <meta property="og:title" content="Brokerage Contact | AptoWebsites" />
                <meta property="og:description" content="Reach out to AptoWebsites for custom brokerage solutions and pricing." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://aptowebsites.com/contact" />
                <meta property="og:image" content="https://aptowebsites.com/og-image.jpg" />
                <link rel="canonical" href="https://aptowebsites.com/contact" />
            </Helmet>
            <main className="broker-message-container" aria-label="Brokerage Contact Form Section">
                <section className="broker-message-card">
                    <h1>Contact for Brokerage Pricing</h1>
                    <form onSubmit={handleSubmit} aria-label="Brokerage Contact Form">
                        <div className="form-group">
                            <label htmlFor="name">Name</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                placeholder="Your Name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                aria-required="true"
                                autoComplete="name"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                placeholder="Your Email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                aria-required="true"
                                autoComplete="email"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="phone">Phone Number</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                placeholder="Your Phone Number"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                                aria-required="true"
                                autoComplete="tel"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="company">Company</label>
                            <input
                                type="text"
                                id="company"
                                name="company"
                                placeholder="Your Company"
                                value={formData.company}
                                onChange={handleChange}
                                required
                                aria-required="true"
                                autoComplete="organization"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="message">Message</label>
                            <textarea
                                id="message"
                                name="message"
                                placeholder="Tell us about your brokerage needs..."
                                value={formData.message}
                                onChange={handleChange}
                                required
                                aria-required="true"
                                rows={4}
                                style={{ resize: "vertical" }}
                            />
                        </div>
                        <button type="submit" className="broker-message-btn" disabled={isSubmitting} aria-busy={isSubmitting}>
                            {isSubmitting ? "Submitting..." : "Contact Us"}
                        </button>
                    </form>
                    {error && <p className="error-message" role="alert">{error}</p>}
                    {successMessage && <p className="success-message" role="status">{successMessage}</p>}
                </section>
            </main>
        </>
    );
};

export default BrokerMessage;
